import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL } from '../config';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [polling, setPolling] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  useEffect(() => {
    const executeSubscription = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid payment token');
        setProcessing(false);
        return;
      }
      
      try {
        await axios.get(`${API_URL}/subscription/execute?token=${token}`);
        setProcessing(false);
        setPolling(true);
        pollSubscriptionStatus();
      } catch (err) {
        setError('Failed to validate subscription');
        setProcessing(false);
      }
    };
    
    executeSubscription();
  }, [searchParams]);
  
  const pollSubscriptionStatus = async () => {
    const maxPolls = 20;
    const pollInterval = 3000;
    
    const checkStatus = async (count) => {
      if (count >= maxPolls) {
        setPolling(false);
        setError('Payment confirmation is taking longer than expected. Please check your profile in a few minutes.');
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.is_premium) {
          setPolling(false);
          setActivated(true);
        } else {
          setPollCount(count + 1);
          setTimeout(() => checkStatus(count + 1), pollInterval);
        }
      } catch (err) {
        setPollCount(count + 1);
        setTimeout(() => checkStatus(count + 1), pollInterval);
      }
    };
    
    checkStatus(0);
  };
  
  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#EE1C4E] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Validating your subscription...</p>
        </div>
      </div>
    );
  }
  
  if (polling) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="max-w-md w-full p-8 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
          <div className="text-center">
            <Clock className="w-16 h-16 text-[#EE1C4E] mx-auto mb-4 animate-pulse" />
            <h1 className="text-2xl font-bold text-white mb-2">Confirming Payment</h1>
            <p className="text-gray-400 mb-4">Your payment is being processed by PayPal. This usually takes a few seconds.</p>
            <div className="bg-[#1A1A1A] rounded-lg p-4 text-sm text-gray-400">
              <p>⏳ Waiting for payment confirmation... ({pollCount}/20)</p>
              <p className="mt-2 text-xs">Please don't close this window.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="max-w-md w-full p-8 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/profile')} className="w-full bg-[#EE1C4E] hover:bg-[#D91744] text-white">
              Go to Profile
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  if (activated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="max-w-md w-full p-8 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to Premium! 🎉</h1>
            <p className="text-gray-400 mb-4">Your subscription has been activated successfully.</p>
            <div className="bg-[#1A1A1A] rounded-lg p-4 mb-6 text-left">
              <h3 className="text-white font-semibold mb-2">Your Benefits:</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>✅ Ad-free experience</li>
                <li>✅ Access to all premium content</li>
                <li>✅ Exclusive features and updates</li>
              </ul>
            </div>
            <div className="space-y-3">
              <Button onClick={() => navigate('/profile')} className="w-full bg-[#EE1C4E] hover:bg-[#D91744] text-white">
                Go to Profile
              </Button>
              <Button onClick={() => navigate('/feed/home')} variant="outline" className="w-full bg-transparent border-gray-700 text-white hover:bg-[#1A1A1A]">
                Browse Content
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default PaymentSuccessPage;
